import { Component, Inject } from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { BehaviorSubject } from 'rxjs';

export class ItemFlatNode {
  displayName?: string;
  level?: number;
  agencyId?: string;
  expandable?: boolean;
}

export class ItemNode {
  children!: ItemNode[];
  displayName!: string;
  agencyId?: string;
}

@Component({
  selector: 'app-dialog-select-agency',
  templateUrl: './dialog-select-agency.component.html',
  styleUrls: ['./dialog-select-agency.component.scss']
})
export class DialogSelectAgencyComponent {
  flatNodeMap = new Map<ItemFlatNode, ItemNode>();
  nestedNodeMap = new Map<ItemNode, ItemFlatNode>();
  selectedParent: ItemFlatNode | null = null;
  treeControl: FlatTreeControl<ItemFlatNode>;
  treeFlattener!: MatTreeFlattener<ItemNode, ItemFlatNode>;
  dataSource!: MatTreeFlatDataSource<ItemNode, ItemFlatNode>;
  checklistSelection = new SelectionModel<ItemFlatNode>(true);
  dataChange = new BehaviorSubject<ItemNode[]>([]);
  dataService: ItemNode[] = [];

  constructor(
    public dialogRef: MatDialogRef<DialogSelectAgencyComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    if (data) {
      this.initialize(data);
    }

    this.treeFlattener = new MatTreeFlattener(this.transformer, this.getLevel, this.isExpandable, this.getChildren);
    this.treeControl = new FlatTreeControl<ItemFlatNode>(this.getLevel, this.isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    this.dataChange.subscribe((data) => {
      this.dataSource.data = data;
    });
  }

  initialize(data: any) {
    this.dataService = this.buildFileTree(data);
    // Notify the change.
    this.dataChange.next(this.dataService);
  }

  getLevel = (node: ItemFlatNode) => node.level || 0;

  getChildren = (node: ItemNode): any[] => node.children;

  hasNoContent = (_: number, _nodeData: ItemFlatNode) => _nodeData.displayName === '';

  onClickBack() {
    this.dialogRef.close();
  }

  // Filtrer lors du changement du code
  nameChanged(target: any) {
    let res: any[] = [];
    if (this.dataService.length > 0 && target?.value?.length > 1) {
      let items = this.dataService.filter((c: ItemNode) => {
        return c.displayName?.toLowerCase().includes(target?.value?.toLowerCase()) && c.agencyId != null;
      });

      if (items && items.length > 0) {
        res = [...res, ...items];
      }
      this.dataChange.next(res);
    }

    if (this.dataService.length > 0 && target?.value?.length < 2) {
      this.dataChange.next(this.dataService);
    }
  }

  isExpandable = (node: ItemFlatNode) => node.expandable || false;

  transformer = (node: ItemNode, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode = existingNode && existingNode.displayName === node.displayName ? existingNode : new ItemFlatNode();
    flatNode.displayName = node.displayName;
    flatNode.level = level;
    flatNode.agencyId = node.agencyId;
    flatNode.expandable = !!node.children?.length;
    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  };

  leafItemSelectionToggle(node: ItemFlatNode): void {
    this.checklistSelection.toggle(node);
    this.checkAllParentsSelection(node);
  }

  checkAllParentsSelection(node: ItemFlatNode): void {
    let parent: ItemFlatNode | null = this.getParentNode(node);
    while (parent) {
      this.checkRootNodeSelection(parent);
      parent = this.getParentNode(parent);
    }
  }

  checkRootNodeSelection(node: ItemFlatNode): void {
    const nodeSelected = this.checklistSelection.isSelected(node);
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected =
      descendants.length > 0 &&
      descendants.every((child) => {
        return this.checklistSelection.isSelected(child);
      });
    if (nodeSelected && !descAllSelected) {
      this.checklistSelection.deselect(node);
    } else if (!nodeSelected && descAllSelected) {
      this.checklistSelection.select(node);
    }
  }

  getParentNode(node: ItemFlatNode): ItemFlatNode | null {
    const currentLevel = this.getLevel(node);

    if (currentLevel && currentLevel < 1) {
      return null;
    }

    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];

      if (currentLevel && this.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  descendantsAllSelected(node: ItemFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected =
      descendants.length > 0 &&
      descendants.every((child) => {
        return this.checklistSelection.isSelected(child);
      });
    return descAllSelected;
  }

  descendantsPartiallySelected(node: ItemFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const result = descendants.some((child) => this.checklistSelection.isSelected(child));
    return result && !this.descendantsAllSelected(node);
  }

  itemSelectionToggle(node: ItemFlatNode): void {
    this.checklistSelection.toggle(node);
    const descendants = this.treeControl.getDescendants(node);
    this.checklistSelection.isSelected(node) ? this.checklistSelection.select(...descendants) : this.checklistSelection.deselect(...descendants);

    descendants.forEach((child) => this.checklistSelection.isSelected(child));
    this.checkAllParentsSelection(node);
  }

  hasChild = (_: number, _nodeData: ItemFlatNode) => _nodeData.expandable;

  buildFileTree(obj: { [key: string]: any }, level: number = 0): ItemNode[] {
    return Object.keys(obj).reduce<ItemNode[]>((accumulator, key) => {
      const value = obj[key];
      const node = new ItemNode();

      node.displayName = value?.displayName;
      node.agencyId = value?.agencyId;

      return accumulator.concat(node);
    }, []);
  }
}
